import { swiper_update_height, new_swiper } from "../sliders/sliders.js";
import { onObject } from "../sliders/onObject.js";
import { toSVG } from "./toSVG.js";
import { updateHiddenGamesCount, setTournamentFiltersActive } from "./gameFilters.js";
import { translate } from "./translations.js";
import { displayMessage } from "../alert/messageFunc.js";

// Debug Configuration
const DEBUG = false; // Set to true to enable console output

// Configuration and Constants
const CONFIG = {
    CACHE_TIMEOUT: 10000, // 10 seconds
    LOAD_MORE: {
        DEFAULT_PER_PAGE: 20,
        MAX_FINISHED_GAMES: 10
    },
    SELECTORS: {
        LOAD_MORE_BTN: '.btn-load',
        LIVE_CONTAINER: '.live_tournaments__js',
        UPCOMING_CONTAINER: '.upcoming_tournaments__js',
        FINISHED_CONTAINER: '.ended_tournaments__js',
        EMPTY_PLACEHOLDER: '.empty_placeholder',
        FILTER_CHECKBOX: '.filter-games-checkbox>input:checked',
        GAMES_SELECTOR: '.games',
        SWIPER_TAB: '.swiper-tab',
        SHOW_ALL_MATCHES: '.show_all_matches'
    },
    GAME_STATUS: {
        UPCOMING: ['0', 0],
        LIVE: ['1', 1],
        FINISHED: ['2', 2, '3', 3]
    },
    IMAGE_BASE_URLS: {
        TEAMS: 'https://img.bets4.info/teams',
        LEAGUES: 'https://img.bets4.info/leagues'
    }
};

// Status Helper Functions
const isLiveGame = (status) => CONFIG.GAME_STATUS.LIVE.includes(status);
const isFinishedGame = (status) => CONFIG.GAME_STATUS.FINISHED.includes(status);
const isUpcomingGame = (status) => CONFIG.GAME_STATUS.UPCOMING.includes(status);

// Utility Functions
const ensureInteger = (value, defaultValue = 1) => {
    const parsed = parseInt(value);
    return isNaN(parsed) ? defaultValue : Math.max(1, parsed);
};

const slugify = (text) => {
    return text.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
};

// Tournament Cache Class
class TournamentCache {
    constructor() {
        this.allGames = [];
        this.lastFetch = null;
        this.cacheTimeout = CONFIG.CACHE_TIMEOUT;
    }

    canUseCache() {
        const now = Date.now();
        const hasCachedData = this.allGames && this.allGames.data && this.allGames.data.length > 0;
        const isNotExpired = this.lastFetch && (now - this.lastFetch) < this.cacheTimeout;
        return hasCachedData && isNotExpired;
    }

    store(data) {
        this.allGames = data;
        this.lastFetch = Date.now();
    }

    clear() {
        this.allGames = [];
        this.lastFetch = null;
    }
}

// Error Handling Class
class TournamentError extends Error {
    constructor(message, originalError = null, context = {}) {
        super(message);
        this.name = 'TournamentError';
        this.originalError = originalError;
        this.context = context;
        this.timestamp = new Date().toISOString();
    }
}

// Game Info Parser Class
class GameInfoParser {
    static parseGameInfo(tournament) {
        return {
            id: tournament.id,
            originalId: tournament.originalId, // For extracted bet types, use original tournament ID in URLs
            gameTextId: tournament.game,
            status: tournament.tournament_status,
            team1: tournament.team_1,
            team2: tournament.team_2,
            tournament: tournament.tournament,
            format: tournament.short_format,
            team1Score: tournament.team_1_score,
            team2Score: tournament.team_2_score,
            team1Img: tournament.team_1_img,
            team2Img: tournament.team_2_img,
            team1ImgUrl: tournament.team_1_img_url,
            team2ImgUrl: tournament.team_2_img_url,
            tournamentImg: tournament.tournament_img,
            tournamentImgUrl: tournament.tournament_img_url,
            timestampStart: tournament.corrected_timestamp_start,
            timestampStarted: tournament.timestamp_started,
            timestampEnd: tournament.timestamp_end,
            timeTo: tournament.time_to,
            betTypes: tournament.bets_type || [],
            _isExtractedBetType: tournament._isExtractedBetType || false,
            countUserBetsInMatch: tournament.count_user_bets_in_match || 0
        };
    }

    static getStatusInfo(gameInfo) {
        const status = gameInfo.status;
        const now = new Date();
        let statusClass = "";
        let timeDisplay = "";
        let isFinished = false;

        // Check if this is an extracted bet type from a live game
        if (gameInfo._isExtractedBetType) {
            statusClass = "time-item";
            timeDisplay = translate("coming soon");
            return { statusClass, timeDisplay, isFinished };
        }

        if (isLiveGame(status)) {
            statusClass = "live min";
            const startTime = new Date((gameInfo.timestampStarted || gameInfo.timeTo) * 1000);
            const timeDiff = now - startTime;
            const minutes = Math.floor(timeDiff / (1000 * 60));
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;
            
            if (hours > 0) {
                timeDisplay = `${String(hours).padStart(2, '0')}:${String(remainingMinutes).padStart(2, '0')} ${translate("ago")}`;
            } else {
                timeDisplay = `00:${String(minutes).padStart(2, '0')} ${translate("ago")}`;
            }
        } else if (isFinishedGame(status)) {
            statusClass = "";
            const timestampToUse = gameInfo.timestampEnd || gameInfo.timeTo;
            timeDisplay = TimeFormatter.formatFinishedGameTime(timestampToUse);
            isFinished = true;
        } else if (isUpcomingGame(status)) {
            statusClass = "time-item";
            const timestampToUse = gameInfo.timestampStart || gameInfo.timeTo;
            
            // Check if timestampStart has already passed
            if (gameInfo.timestampStart && gameInfo.timestampStart * 1000 < now.getTime()) {
                timeDisplay = translate("coming soon");
            } else {
                timeDisplay = TimeFormatter.formatTime(timestampToUse);
            }
        } else {
            statusClass = "time-item";
            timeDisplay = TimeFormatter.formatTime(gameInfo.timeTo);
        }

        return { statusClass, timeDisplay, isFinished };
    }

    static getTeamDisplayInfo(gameInfo, isFinished) {
        let team1Classes = "games-card-left games-card-item";
        let team2Classes = "games-card-right games-card-item";
        let team1TitleClasses = "games-card-title";
        let team2TitleClasses = "games-card-title";
        let team1NameWrapper = "";
        let team2NameWrapper = "";

        if (isFinished) {
            const team1Score = parseInt(gameInfo.team1Score) || 0;
            const team2Score = parseInt(gameInfo.team2Score) || 0;

            if (team1Score > team2Score) {
                team2Classes += " lose";
                team1TitleClasses += " crown-icon c-important left";
                team1NameWrapper = `<b class="nowrap">${gameInfo.team1}</b>`;
                team2NameWrapper = `<span>${gameInfo.team2}</span>`;
            } else if (team2Score > team1Score) {
                team1Classes += " lose";
                team2TitleClasses += " crown-icon c-important";
                team1NameWrapper = `<b class="nowrap">${gameInfo.team1}</b>`;
                team2NameWrapper = `<span>${gameInfo.team2}</span>`;
            } else {
                team1NameWrapper = `<b class="nowrap">${gameInfo.team1}</b>`;
                team2NameWrapper = `<span>${gameInfo.team2}</span>`;
            }
        } else {
            team1NameWrapper = `<span>${gameInfo.team1}</span>`;
            team2NameWrapper = `<span>${gameInfo.team2}</span>`;
        }

        return {
            team1Classes,
            team2Classes,
            team1TitleClasses,
            team2TitleClasses,
            team1NameWrapper,
            team2NameWrapper
        };
    }
}

// Time Formatter Class
class TimeFormatter {
    static formatTime(timestamp) {
        const gameTime = new Date(timestamp * 1000);
        const now = new Date();
        const diffMs = gameTime.getTime() - now.getTime();
        
        if (diffMs > 0) {
            const diffMinutes = Math.floor(diffMs / (1000 * 60));
            const hours = Math.floor(diffMinutes / 60);
            const minutes = diffMinutes % 60;
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${translate("from now")}`;
        } else {
            return gameTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
    }

    static formatFinishedGameTime(timestamp) {
        const date = new Date(timestamp * 1000);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${day}.${month}.${year} ${hours}:${minutes}`;
    }
}

// Game Asset Helper Class
class GameAssetHelper {
    static getGameIconClass(gameTextId) {
        const gameClassMap = {
            'dota2': 'dota-bc',
            'cs_go': 'cs-bc',
            'cs2': 'cs-bc',
            'overwatch': 'ow-bc',
            'hs': 'hs-bc',
            'soccer': 'fb-bc',
            'basketball': 'bb-bc',
            'hockey': 'hk-bc',
            'lol': 'lol-bc',
            'sc': 'sc2-bc',
            'valorant': 'vl-bc',
            'mma': 'mma-bc',
            'boxing': 'boxing-bc',
            'other': 'other-bc'
        };
        return gameClassMap[gameTextId] || 'other-bc';
    }

    static getGameTitle(gameTextId) {
        const gameTitleMap = {
            'dota2': translate('Dota 2'),
            'cs_go': translate('CS 2'),
            'cs2': translate('CS 2'),
            'overwatch': translate('Overwatch'),
            'hs': translate('Hearthstone'),
            'soccer': translate('Football'),
            'basketball': translate('Basketball'),
            'hockey': translate('Hockey'),
            'lol': translate('League of Legends'),
            'sc': translate('StarCraft'),
            'valorant': translate('Valorant'),
            'mma': translate('MMA'),
            'boxing': translate('Boxing'),
            'other': translate('Other games')
        };
        return gameTitleMap[gameTextId] || translate('Other games');
    }

    static getTeamImageUrl(imageId, gameTextId) {
        if (!imageId) return `${CONFIG.IMAGE_BASE_URLS.TEAMS}/${gameTextId}/team_no_img.png`;
        return `${CONFIG.IMAGE_BASE_URLS.TEAMS}/${gameTextId}/${imageId}.png`;
    }

    static getTournamentImageUrl(imageId, gameTextId) {
        if (!imageId) return `${CONFIG.IMAGE_BASE_URLS.LEAGUES}/${gameTextId}/default.png`;
        return `${CONFIG.IMAGE_BASE_URLS.LEAGUES}/${gameTextId}/${imageId}.png`;
    }

    static getBetTypeRate(betTypes, rateType) {
        if (!betTypes || betTypes.length === 0) return 50;
        const rate = betTypes[0][rateType];
        return rate !== undefined && rate !== null ? rate : 50;
    }

    static getScoreDisplay(score1, score2) {
        if (score1 === score2 && score1 === "0") return "VS";
        return `${score1}:${score2}`;
    }
}

// Cache for storing all tournament data
let tournamentCache = {
    allGames: [],
    lastFetch: null,
    cacheTimeout: 10000 // 10 seconds cache timeout
};

// HTML Template Builders
class HtmlTemplateBuilder {
    static createTeamSection(gameInfo, teamInfo, isFinished, rateType, isLeft = true) {
        const teamName = isLeft ? gameInfo.team1 : gameInfo.team2;
        const teamImg = isLeft ? (gameInfo.team1ImgUrl || GameAssetHelper.getTeamImageUrl(gameInfo.team1Img, gameInfo.gameTextId)) : 
                                 (gameInfo.team2ImgUrl || GameAssetHelper.getTeamImageUrl(gameInfo.team2Img, gameInfo.gameTextId));
        const classes = isLeft ? teamInfo.team1Classes : teamInfo.team2Classes;
        const titleClasses = isLeft ? teamInfo.team1TitleClasses : teamInfo.team2TitleClasses;
        const nameWrapper = isLeft ? teamInfo.team1NameWrapper : teamInfo.team2NameWrapper;
        const filteredBetTypes = gameInfo.betTypes;

        const titleElement = `
            <span class="${titleClasses}">
                ${nameWrapper}
                ${!isFinished ? `<span class="${rateType}">${GameAssetHelper.getBetTypeRate(filteredBetTypes, rateType)}%</span>` : ''}
            </span>`;
        
        const imgElement = `
            <span class="games-card-img">
                <img class="placeholder" sizes="(max-width: 991px) 30px, 58px" loading="lazy" src="${teamImg}" alt="${teamName}">
            </span>`;

        // For right team (games-card-right), place img before title
        const content = isLeft ? `${titleElement}${imgElement}` : `${imgElement}${titleElement}`;

        return `
            <span class="${classes}">
                ${content}
            </span>`;
    }

    static createBetTypesSection(betTypes, gameStatus) {
        if (!betTypes || betTypes.length === 0) {
            return '';
        }

        // Filter bet types based on game status
        let filteredBetTypes = betTypes;
        let shouldShowSwiper = true;
        
        if (isLiveGame(gameStatus)) {
            const liveBetTypes = betTypes.filter(betType => betType.live === 1);
            
            if (liveBetTypes.length > 0) {
                // Has live bet types, show them with swiper
                const status0BetTypes = betTypes.filter(betType => 
                    betType.live !== 1 && (betType.status === 0 || betType.status === "0")
                );
                filteredBetTypes = [...liveBetTypes, ...status0BetTypes];
            } else {
                // No live bet types, look for default match bet type
                const matchBetType = betTypes.find(betType => 
                    betType.type === 'match' || 
                    betType.show_type === 'match' ||
                    (betType.type_data && betType.type_data.title === 'match')
                );
                
                if (matchBetType) {
                    filteredBetTypes = [matchBetType];
                    shouldShowSwiper = false; // Don't show swiper for single default match
                } else {
                    // Fallback: use first available bet type without swiper
                    filteredBetTypes = [betTypes[0]];
                    shouldShowSwiper = false;
                }
            }
        }

        // If we shouldn't show swiper (single default bet type), return empty string
        if (!shouldShowSwiper) {
            return '';
        }

        const wrapperId = `swiper-wrapper-${Math.random().toString(36).substr(2, 9)}`;
        const betTypesContent = filteredBetTypes.map((betType, index) => 
            HtmlTemplateBuilder.createBetTypeSlide(betType, index, filteredBetTypes.length)
        ).join('');

        return `
            <div class="games-top-tabs bookmark">
                <div class="swiper swiper-tab swiper-initialized swiper-horizontal swiper-pointer-events swiper-free-mode swiper-backface-hidden" data-position="hide">
                    <div class="swiper-wrapper" style="transform: translate3d(0px, 0px, 0px);" id="${wrapperId}" aria-live="polite">
                        ${betTypesContent}
                    </div>
                <span class="swiper-notification" aria-live="assertive" aria-atomic="true"></span></div>
            </div>`;
    }

    static createBetTypeSlide(betType, index, totalCount) {
        const activeClass = index === 0 ? 'active' : '';
        const swiperClasses = index === 0 ? 'swiper-slide-active' : (index === 1 ? 'swiper-slide-next' : '');
        const zIndex = index === 0 ? 0 : -index;
        const dataGame = JSON.stringify({
            team_1_rate: betType.team_1_rate,
            team_2_rate: betType.team_2_rate
        }).replace(/"/g, '&quot;');

        const { imgContent, betTypeText } = HtmlTemplateBuilder.getBetTypeContent(betType);
        const isLive = betType.live === 1 || betType.live === true || 
                      betType.status === 'live' || betType.status === '1' ||
                      (betType.type && betType.type.includes('live'));
        const liveIcon = isLive ? '<span class="live live--icon-like"></span>' : '';

        return `
            <div class="swiper-slide tabs__item ${activeClass} js-game ${swiperClasses}" data-game="${dataGame}" role="group" aria-label="${index + 1} / ${totalCount}" style="z-index: ${zIndex};">
                ${liveIcon}
                ${imgContent}
                <span>${betTypeText}</span>
            </div>`;
    }

    static getBetTypeContent(betType) {
        let imgContent = '';
        let betTypeText = '';

        if (betType.type_data) {
            betTypeText = betType.type_data.title || betType.type || 'match';
            const iconPath = betType.type_data.icon;
            
            if (iconPath) {
                imgContent = `<img class="icon to-svg" src="${iconPath}" alt="${betTypeText}" width="17" height="17">`;
            } else {
                imgContent = `<img class="icon to-svg" src="/template/images/svg/games/match.svg" alt="match" width="17" height="17">`;
            }
        } else {
            betTypeText = betType.show_type || betType.type || 'match';
            imgContent = HtmlTemplateBuilder.getLegacyBetTypeIcon(betTypeText);
        }

        return { imgContent, betTypeText };
    }

    static getLegacyBetTypeIcon(betTypeText) {
        if (betTypeText.includes('1st game') || betTypeText === '1st game') {
            return `<img class="icon to-svg" src="/template/images/svg/games/1.svg" alt="1st game" width="17" height="17">`;
        } else if (betTypeText.includes('2nd game') || betTypeText === '2nd game') {
            return `<img class="icon to-svg" src="/template/images/svg/games/2.svg" alt="2nd game" width="17" height="17">`;
        } else if (betTypeText.includes('3rd game') || betTypeText === '3rd game') {
            return `<img class="icon to-svg" src="/template/images/svg/games/3.svg" alt="3rd game" width="17" height="17">`;
        } else if (betTypeText.includes('4th game') || betTypeText === '4th game') {
            return `<img class="icon to-svg" src="/template/images/svg/games/4.svg" alt="4th game" width="17" height="17">`;
        } else if (betTypeText.includes('5th game') || betTypeText === '5th game') {
            return `<img class="icon to-svg" src="/template/images/svg/games/5.svg" alt="5th game" width="17" height="17">`;
        } else if (betTypeText.includes('hc ') && betTypeText.includes(' + ')) {
            if (betTypeText.includes('+ 1.5')) {
                return `<img class="icon to-svg" src="/template/images/svg/games/hc.svg" alt="handicap" width="17" height="17">`;
            } else {
                return `<img class="icon to-svg" src="/template/images/svg/games/hc_reverted.svg" alt="handicap reverted" width="17" height="17">`;
            }
        } else {
            return `<img class="icon to-svg" src="/template/images/svg/games/match.svg" alt="match" width="17" height="17">`;
        }
    }

    static buildGameHtml(tournament) {
        const gameInfo = GameInfoParser.parseGameInfo(tournament);
        const statusInfo = GameInfoParser.getStatusInfo(gameInfo);
        const teamInfo = GameInfoParser.getTeamDisplayInfo(gameInfo, statusInfo.isFinished);
        
        // Filter bet types for display and rate calculation
        let filteredBetTypes = gameInfo.betTypes;
        let shouldShowSwiper = true;
        
        // Don't show swiper for extracted bet types from live games
        if (gameInfo._isExtractedBetType) {
            shouldShowSwiper = false;
        } else if (isLiveGame(gameInfo.status)) {
            const liveBetTypes = gameInfo.betTypes.filter(betType => betType.live === 1);
            
            if (liveBetTypes.length > 0) {
                // Has live bet types, show them with swiper
                const status0BetTypes = gameInfo.betTypes.filter(betType => 
                    betType.live !== 1 && (betType.status === 0 || betType.status === "0")
                );
                filteredBetTypes = [...liveBetTypes, ...status0BetTypes];
            } else {
                // No live bet types, look for default match bet type
                const matchBetType = gameInfo.betTypes.find(betType => 
                    betType.type === 'match' || 
                    betType.show_type === 'match' ||
                    (betType.type_data && betType.type_data.title === 'match')
                );
                
                if (matchBetType) {
                    filteredBetTypes = [matchBetType];
                    shouldShowSwiper = false; // Don't show swiper for single default match
                } else {
                    // Fallback: use first available bet type without swiper
                    filteredBetTypes = [gameInfo.betTypes[0]];
                    shouldShowSwiper = false;
                }
            }
        }

        // Update gameInfo with filtered bet types for rate calculation
        gameInfo.betTypes = filteredBetTypes;

        // Only create bet types section if we should show swiper or if it's not finished and has bet types
        const betTypesSection = !statusInfo.isFinished && filteredBetTypes.length > 0 && shouldShowSwiper ? 
            HtmlTemplateBuilder.createBetTypesSection(filteredBetTypes, gameInfo.status) : '';
        
        const gamesClass = statusInfo.isFinished ? "games min right" : "games";
        const tournamentImg = gameInfo.tournamentImgUrl || GameAssetHelper.getTournamentImageUrl(gameInfo.tournamentImg, gameInfo.gameTextId);
        const gamesCardClass = gameInfo.countUserBetsInMatch > 0 ? "games-card betted-icon" : "games-card";
        
        // Use original ID for URL if available (for extracted bet types), otherwise use regular ID
        const urlId = gameInfo.originalId || gameInfo.id;

        return `<div class="${gamesClass}" data-game-text-id="${gameInfo.gameTextId}" data-game-id="${gameInfo.id}">
                        <div class="games-top">
                            <div class="games-top-time ${statusInfo.statusClass}"><span>${statusInfo.timeDisplay}</span></div>
                             ${betTypesSection}
                            
                            <div class="games-top-name">${gameInfo.tournament}</div>
                        </div>
                        <a href="/tournament-${urlId}/${slugify(gameInfo.team1)}-${slugify(gameInfo.team2)}/" class="${gamesCardClass}">
                            <span class="games-card-icon ${GameAssetHelper.getGameIconClass(gameInfo.gameTextId)}" alt="${GameAssetHelper.getGameTitle(gameInfo.gameTextId)}"></span>
                            <span class="games-card-info">
                            <span class="games-card-wrap">
                                ${HtmlTemplateBuilder.createTeamSection(gameInfo, teamInfo, statusInfo.isFinished, 'team_1_rate', true)}
                                <span class="games-card-center games-card-item">
                                    <span class="games-card-score">
                                        <b class="team_score">${GameAssetHelper.getScoreDisplay(gameInfo.team1Score, gameInfo.team2Score)}</b>
                                        ${!statusInfo.isFinished ? `<span>${gameInfo.format}</span>` : ''}
                                    </span>
                                </span>
                                ${HtmlTemplateBuilder.createTeamSection(gameInfo, teamInfo, statusInfo.isFinished, 'team_2_rate', false)}
                            </span>
                            ${!statusInfo.isFinished ? `<span class="games-card-image">
                                <span class="games-card-img">
                                    <img class="placeholder" sizes="(max-width: 991px) 30px, 58px" loading="lazy" src="${tournamentImg}" alt="${gameInfo.tournament}">
                                </span>
                            </span>` : ''}
                        </span>
                        </a>
                    </div>`;
    }
}

// Game Categorizer Class
class GameCategorizer {
    constructor(games) {
        this.games = games;
    }

    get liveGames() {
        return this.games
            .filter(game => isLiveGame(game.tournament_status))
            .sort((a, b) => {
                // First sort by timestamp_started ASC
                const timestampDiff = (a.timestamp_started || 0) - (b.timestamp_started || 0);
                if (timestampDiff !== 0) return timestampDiff;
                // Then by id ASC
                return (parseInt(a.id) || 0) - (parseInt(b.id) || 0);
            });
    }

    get upcomingGames() {
        return this.games
            .filter(game => isUpcomingGame(game.tournament_status))
            .sort((a, b) => {
                // First sort by timestamp_start ASC
                const timestampDiff = (a.timestamp_start || 0) - (b.timestamp_start || 0);
                if (timestampDiff !== 0) return timestampDiff;
                // Then by id ASC
                return (parseInt(a.id) || 0) - (parseInt(b.id) || 0);
            });
    }

    get finishedGames() {
        return this.games
            .filter(game => isFinishedGame(game.tournament_status))
            .sort((a, b) => {
                // First sort by timestamp_end DESC
                const timestampDiff = (b.timestamp_end || 0) - (a.timestamp_end || 0);
                if (timestampDiff !== 0) return timestampDiff;
                // Then by id ASC
                return (parseInt(a.id) || 0) - (parseInt(b.id) || 0);
            });
    }

    applyFilters(activeFilters) {
        if (activeFilters.length === 0) return this;

        const filteredGames = this.games.filter(game => 
            activeFilters.includes(game.game) || game.game === 'other'
        );

        return new GameCategorizer(filteredGames);
    }
}

// Live Game Bet Type Extractor Class
class LiveGameBetTypeExtractor {
    static extractUpcomingBetTypes(liveGames) {
        const upcomingBetTypes = [];
        
        liveGames.forEach(liveGame => {
            if (!liveGame.bets_type || liveGame.bets_type.length === 0) return;
            
            const upcomingBetType = this.findNextUpcomingBetType(liveGame.bets_type);
            if (upcomingBetType) {
                // Create a new tournament entry for the upcoming bet type
                const upcomingTournament = this.createUpcomingTournamentFromBetType(liveGame, upcomingBetType);
                upcomingBetTypes.push(upcomingTournament);
            }
        });
        
        return upcomingBetTypes;
    }
    
    static findNextUpcomingBetType(betTypes) {
        // Sort bet types by their sequence (game1, game2, etc.)
        const sortedBetTypes = betTypes
            .filter(betType => this.isSequentialBetType(betType))
            .sort((a, b) => this.getBetTypeSequence(a) - this.getBetTypeSequence(b));
        
        if (sortedBetTypes.length === 0) return null;
        
        // Find the first upcoming bet type that comes after all finished games
        // and has no live games before it
        let lastFinishedIndex = -1;
        let hasLiveGame = false;
        
        for (let i = 0; i < sortedBetTypes.length; i++) {
            const betType = sortedBetTypes[i];
            const status = betType.status;
            
            if (status === 3 || status === "3"
                || status === 22 || status === "22"
            ) {
                lastFinishedIndex = i;
            } else if (status === 1 || status === "1") {
                hasLiveGame = true;
            } else if (status === 0 || status === "0") {
                // This is an upcoming bet type
                // Only return it if:
                // 1. There are finished games before it (lastFinishedIndex >= 0)
                // 2. There's no live game in between
                if (lastFinishedIndex >= 0 && !hasLiveGame) {
                    return betType;
                }
                break; // Stop at first upcoming game
            }
        }
        
        return null;
    }
    
    static isSequentialBetType(betType) {
        const betTypeText = betType.type || betType.show_type || '';
        return /^(game\s*\d+|\d+(st|nd|rd|th)\s*game)$/i.test(betTypeText.trim());
    }
    
    static getBetTypeSequence(betType) {
        const betTypeText = betType.type || betType.show_type || '';
        const match = betTypeText.match(/(\d+)/);
        return match ? parseInt(match[1]) : 999;
    }
    
    static createUpcomingTournamentFromBetType(baseTournament, betType) {
        return {
            ...baseTournament,
            id: `${baseTournament.id}-${betType.type || betType.show_type}`,
            originalId: baseTournament.id, // Store original ID for URL generation
            tournament_status: 0, // Set as upcoming
            timestamp_start: null, // Will show "coming soon"
            time_to: null, // Will show "coming soon"
            format: betType.type_data?.title || betType.type || betType.show_type || translate('Next Game'),
            short_format: betType.type_data?.title || betType.type || betType.show_type || translate('Next Game'),
            bets_type: [betType], // Only include the specific upcoming bet type
            _isExtractedBetType: true // Flag to identify these special cases
        };
    }
}

// Load More State Management Class
class LoadMoreState {
    constructor() {
        this.currentPage = 1;
        this.hasMoreData = true;
        this.isLoading = false;
        this.totalLoaded = 0;
        this.totalAvailable = 0;
    }

    reset() {
        this.currentPage = 1;
        this.hasMoreData = true;
        this.isLoading = false;
        this.totalLoaded = 0;
        this.totalAvailable = 0;
    }

    nextPage() {
        if (this.hasMoreData && !this.isLoading) {
            this.currentPage++;
            return this.currentPage;
        }
        return null;
    }

    updateState(loadedCount, totalAvailable) {
        this.totalLoaded += loadedCount;
        this.totalAvailable = totalAvailable;
        this.hasMoreData = this.totalLoaded < totalAvailable;
        this.isLoading = false;
    }

    setLoading(loading) {
        this.isLoading = loading;
        
        // Safety check: if setting loading to false, ensure any button state is also reset
        if (!loading && typeof window !== 'undefined' && window.$ && $('.btn-load').length) {
            $('.btn-load').removeClass('disabled load');
        }
    }
}

// Load More Calculator Class
class LoadMoreCalculator {
    constructor(apiResponse, allUpcomingGames) {
        this.hasApiData = apiResponse.pagination && apiResponse.pagination.per_page;
        this.perPage = this.hasApiData ? apiResponse.pagination.per_page : CONFIG.LOAD_MORE.DEFAULT_PER_PAGE;
        this.totalGames = this.hasApiData ? apiResponse.pagination.games : allUpcomingGames.length;
    }

    getPageSlice(games, currentPage) {
        const startIndex = (currentPage - 1) * this.perPage;
        const endIndex = startIndex + this.perPage;
        return games.slice(startIndex, endIndex);
    }

    createLoadMoreInfo(currentPage, filteredUpcoming, totalUpcoming) {
        const totalLoaded = (currentPage - 1) * this.perPage + filteredUpcoming.length;
        return {
            current_page: currentPage,
            per_page: this.perPage,
            games: totalUpcoming,
            total_loaded: totalLoaded,
            has_more: totalLoaded < totalUpcoming,
            loaded_on_page: filteredUpcoming.length
        };
    }
}

// Main Tournament Filters Class
class TournamentFilters {
    constructor() {
        this.cache = new TournamentCache();
        this.loadMoreState = new LoadMoreState();
        this.elements = this.cacheElements();
        this.pendingFilterOperation = null; // Track pending filter operations
        this.filterChangeTimeout = null; // Debounce filter changes
        this.isShowingEmptyState = false; // Track if we're actively showing empty state
    }

    cacheElements() {
        return {
            loadMoreBtn: $(CONFIG.SELECTORS.LOAD_MORE_BTN),
            liveContainer: $(CONFIG.SELECTORS.LIVE_CONTAINER),
            upcomingContainer: $(CONFIG.SELECTORS.UPCOMING_CONTAINER),
            finishedContainer: $(CONFIG.SELECTORS.FINISHED_CONTAINER),
            showAllMatches: $(CONFIG.SELECTORS.SHOW_ALL_MATCHES)
        };
    }

    isTournamentPage() {
        return this.elements.loadMoreBtn.length > 0;
    }

    init() {
        if (!this.isTournamentPage()) return;
        
        // Set tournament filters as active to prevent game filters from overriding hidden count
        setTournamentFiltersActive(true);
        
        this.bindEvents();
        this.setupCleanup();
        this.initializeLoadMoreButton(); // Check if load more should be shown
    }

    bindEvents() {
        document.addEventListener('gameFilterChange', this.handleGameFilterChangeDebounced.bind(this));
        $(document).off('click.tournament').on('click.tournament', CONFIG.SELECTORS.LOAD_MORE_BTN, this.handleLoadMoreClick.bind(this));
    }

    setupCleanup() {
        window.tournamentFiltersCleanup = () => {
            // Cancel any pending operations
            this.cancelPendingOperations();
            
            // Clear any pending toast messages
            this.hideLoadingMessage();
            
            // Deactivate tournament filters management
            setTournamentFiltersActive(false);
            
            document.removeEventListener('gameFilterChange', this.handleGameFilterChangeDebounced.bind(this));
            $(document).off('click.tournament');
            this.destroyAllGameSwipers();
            this.cache.clear();
            this.resetLoadingState();
        };
    }

    // Debounced filter change handler to prevent rapid successive calls
    handleGameFilterChangeDebounced(event) {
        if (!this.isTournamentPage()) return;
        
        if (DEBUG) console.log('Tournament filter change detected:', event.detail);
        
        // Cancel any pending filter operation
        this.cancelPendingOperations();
        
        // Clear any existing timeout
        if (this.filterChangeTimeout) {
            clearTimeout(this.filterChangeTimeout);
        }
        
        // Debounce filter changes by 300ms
        this.filterChangeTimeout = setTimeout(() => {
            this.handleFilterChange();
            this.filterChangeTimeout = null;
        }, 300);
    }

    handleGameFilterChange(event) {
        // Legacy method - now redirects to debounced version
        this.handleGameFilterChangeDebounced(event);
    }

    // Cancel pending operations and reset states
    cancelPendingOperations() {
        if (this.pendingFilterOperation) {
            this.pendingFilterOperation.cancelled = true;
            this.pendingFilterOperation = null;
        }
        
        if (this.filterChangeTimeout) {
            clearTimeout(this.filterChangeTimeout);
            this.filterChangeTimeout = null;
        }
        

        
        // Reset loading state to ensure button is not stuck
        this.resetLoadingState();
    }

    handleLoadMoreClick(e) {
        e.preventDefault();
        e.stopPropagation();
        
        const $btn = $(e.currentTarget);
        
        if ($btn.hasClass('disabled') || this.loadMoreState.isLoading) {
            if (DEBUG) console.log('Load more click ignored - button disabled or loading');
            return false;
        }

        this.loadMoreTournaments();
        return false;
    }

    handleFilterChange() {
        // When filters change, we need to reload from the beginning
        // Keep existing games visible until new ones are loaded
        
        // If already loading, cancel the previous operation and start fresh
        if (this.loadMoreState.isLoading) {
            if (DEBUG) console.log('Filter change during loading - resetting state');
            this.resetLoadingState();
        }
        
        this.loadMoreState.reset();
        this.loadFilteredTournaments();
    }

    async loadFilteredTournaments() {
        // Create operation tracker
        const operation = { cancelled: false };
        this.pendingFilterOperation = operation;
        
        try {
            this.loadMoreState.setLoading(true);
            this.showLoadingState();
            

            
            if (DEBUG) console.log('Loading filtered tournaments from API...');
            
            // Check if operation was cancelled
            if (operation.cancelled) {
                if (DEBUG) console.log('Filter operation cancelled before API call');
                return;
            }
            
            // Fetch fresh data for filtering
            const apiResponse = await this.fetchTournamentData();
            
            // Check if operation was cancelled after API call
            if (operation.cancelled) {
                if (DEBUG) console.log('Filter operation cancelled after API call');
                return;
            }
            
            this.cache.store(apiResponse);
            
            const processedData = this.processGamesByStatus(apiResponse, 1);
            
            // Check if operation was cancelled after processing
            if (operation.cancelled) {
                if (DEBUG) console.log('Filter operation cancelled after processing');
                return;
            }
            
            if (DEBUG) console.log('Received filtered data:', { 
                liveGames: processedData.liveGames.length,
                upcomingGames: processedData.upcomingGames.length,
                finishedGames: processedData.finishedGames.length,
                loadMoreInfo: processedData.loadMoreInfo 
            });
            
            // Only now clear and replace existing games with new filtered results
            this.clearContainers();
            this.updateGamesContainer(processedData, true);
            this.updateLoadMoreButton(processedData.loadMoreInfo);
            
            // Update state for the filtered results
            this.loadMoreState.currentPage = 1;
            this.loadMoreState.totalLoaded = processedData.upcomingGames.length;
            this.loadMoreState.totalAvailable = processedData.totalUpcoming;
            this.loadMoreState.hasMoreData = this.loadMoreState.totalLoaded < processedData.totalUpcoming;
            this.loadMoreState.setLoading(false);
            

            
            swiper_update_height();
            this.initializeGameElements();
            
        } catch (error) {
            // Only handle error if operation wasn't cancelled
            if (!operation.cancelled) {
                this.handleError(error, { context: 'loadFilteredTournaments' });
                // Keep existing games on error - don't clear them
                this.showFilterErrorMessage();
            }
        } finally {
            // Always ensure loading state is reset, even if error handling above fails
            // But only if this operation is still the active one
            if (this.pendingFilterOperation === operation) {
                this.ensureLoadingStateReset();
                this.pendingFilterOperation = null;
            }
        }
    }

    showFilterErrorMessage() {
        // Show a temporary error message without clearing existing games
        if (DEBUG) console.error('Failed to load filtered tournaments, keeping existing games visible');
        
        // Show error message using toast system
        displayMessage(
            translate('Unable to apply filters. Please try again.'),
            'error',
            translate('Error'),
            8 // 8 seconds
        );
        
        // Reset loading state but keep current games - ensure button is enabled
        this.resetLoadingState();
    }

    async loadMoreTournaments() {
        // For load more, we start from page 2 since page 1 is already on the page
        const nextPage = this.loadMoreState.currentPage + 1;
        await this.loadTournamentData(nextPage, false);
    }

    async loadTournamentData(page = 1, isInitial = false) {
        try {
            this.loadMoreState.setLoading(true);
            this.showLoadingState();
            

            
            let apiResponse;
            if (this.cache.canUseCache()) {
                if (DEBUG) console.log('Using cached tournament data for page:', page);
                apiResponse = this.cache.allGames;
            } else {
                if (DEBUG) console.log('Fetching fresh tournament data for page:', page);
                apiResponse = await this.fetchTournamentData();
                this.cache.store(apiResponse);
            }
            
            const processedData = this.processGamesByStatus(apiResponse, page);
            
            if (DEBUG) console.log('Processed data for page:', { 
                requestedPage: page,
                resultPage: processedData.loadMoreInfo.current_page,
                newGames: processedData.upcomingGames.length,
                loadMoreInfo: processedData.loadMoreInfo 
            });
            
            this.updateGamesContainer(processedData, isInitial);
            this.updateLoadMoreButton(processedData.loadMoreInfo);
            
            // Update state based on the page we just loaded
            if (isInitial) {
                // For initial filter-based reload, reset and start from this page
                this.loadMoreState.currentPage = page;
                this.loadMoreState.totalLoaded = processedData.upcomingGames.length;
            } else {
                // For load more, increment normally
                this.loadMoreState.currentPage = page;
                this.loadMoreState.totalLoaded += processedData.upcomingGames.length;
            }
            
            this.loadMoreState.totalAvailable = processedData.totalUpcoming;
            this.loadMoreState.hasMoreData = this.loadMoreState.totalLoaded < processedData.totalUpcoming;
            this.loadMoreState.setLoading(false);
            

            
            swiper_update_height();
            this.initializeGameElements();
            
        } catch (error) {
            this.handleError(error, { page, context: 'loadTournamentData' });
            this.showErrorState();
        } finally {
            // Always ensure loading state is reset, even if error handling above fails
            this.ensureLoadingStateReset();
        }
    }

    async fetchTournamentData() {
        const apiUrl = '/widget/tournaments.php';
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
            throw new TournamentError(`HTTP error! status: ${response.status}`, null, { apiUrl });
        }
        
        return await response.json();
    }

    processGamesByStatus(apiResponse, currentPage) {
        if (DEBUG) console.log('processGamesByStatus called with currentPage:', currentPage);
        
        if (!apiResponse || !apiResponse.data || apiResponse.data.length === 0) {
            return this.createEmptyProcessedData();
        }

        const gameData = apiResponse.data;
        const gameCategorizer = new GameCategorizer(gameData);
        const activeFilters = this.getActiveFilters();
        
        // Get total counts before filtering
        const totalUnfilteredGames = {
            live: gameCategorizer.liveGames.length,
            upcoming: gameCategorizer.upcomingGames.length,
            finished: gameCategorizer.finishedGames.length
        };
        
        // Apply filters to all games first, before any pagination
        const filteredCategorizer = gameCategorizer.applyFilters(activeFilters);
        
        const allLiveGames = filteredCategorizer.liveGames;
        const allUpcomingGames = filteredCategorizer.upcomingGames; // Already filtered
        const allFinishedGames = filteredCategorizer.finishedGames;
        
        // Extract upcoming bet types from live games and apply filters to them
        const allLiveGamesUnfiltered = gameCategorizer.liveGames; // Get unfiltered live games for extraction
        const extractedUpcomingBetTypes = LiveGameBetTypeExtractor.extractUpcomingBetTypes(allLiveGamesUnfiltered);
        
        // Apply filters to extracted bet types
        const filteredExtractedBetTypes = activeFilters.length === 0 ? 
            extractedUpcomingBetTypes :
            extractedUpcomingBetTypes.filter(betType => activeFilters.includes(betType.game));
        
        // Give extracted bet types priority timestamps to ensure they appear at the top
        const prioritizedExtractedBetTypes = filteredExtractedBetTypes.map((betType, index) => ({
            ...betType,
            _priority: index, // Use index to maintain relative order among extracted bet types
            timestamp_start: -1000000 - index // Very negative timestamp to ensure top position
        }));
        
        // Combine extracted bet types first (top), then regular upcoming games (both already filtered)
        const combinedFilteredUpcomingGames = [...prioritizedExtractedBetTypes, ...allUpcomingGames];
        
        // Sort the combined array to ensure extracted bet types with negative timestamps are first
        combinedFilteredUpcomingGames.sort((a, b) => {
            const aTimestamp = a.timestamp_start || 0;
            const bTimestamp = b.timestamp_start || 0;
            return aTimestamp - bTimestamp;
        });
        
        // Calculate hidden games count (only live and upcoming games)
        const totalFilteredLiveAndUpcoming = allLiveGames.length + combinedFilteredUpcomingGames.length;
        const totalUnfilteredLiveAndUpcoming = totalUnfilteredGames.live + totalUnfilteredGames.upcoming;
        const hiddenGamesCount = totalUnfilteredLiveAndUpcoming - totalFilteredLiveAndUpcoming;
        
        if (DEBUG) console.log('Games categorized and filtered:', {
            live: allLiveGames.length,
            upcoming: allUpcomingGames.length,
            extractedUpcoming: extractedUpcomingBetTypes.length,
            filteredExtracted: filteredExtractedBetTypes.length,
            totalFilteredUpcoming: combinedFilteredUpcomingGames.length,
            finished: allFinishedGames.length,
            totalUnfilteredLiveAndUpcoming: totalUnfilteredLiveAndUpcoming,
            totalFilteredLiveAndUpcoming: totalFilteredLiveAndUpcoming,
            hiddenCount: hiddenGamesCount
        });

        // Now paginate the already-filtered results
        const loadMoreCalc = new LoadMoreCalculator(apiResponse, combinedFilteredUpcomingGames);
        const paginatedUpcoming = loadMoreCalc.getPageSlice(combinedFilteredUpcomingGames, currentPage);
        
        // For finished games, always take the most recent ones up to the limit (already filtered)
        const recentFinished = allFinishedGames.slice(0, CONFIG.LOAD_MORE.MAX_FINISHED_GAMES);

        return {
            liveGames: currentPage === 1 ? allLiveGames : [], // Only load live games on initial load
            upcomingGames: paginatedUpcoming,
            finishedGames: currentPage === 1 ? recentFinished : [], // Only load finished games on initial load
            totalUpcoming: combinedFilteredUpcomingGames.length,
            totalCounts: {
                live: allLiveGames.length,
                upcoming: paginatedUpcoming.length,
                finished: recentFinished.length
            },
            loadMoreInfo: loadMoreCalc.createLoadMoreInfo(currentPage, paginatedUpcoming, combinedFilteredUpcomingGames.length),
            hiddenGamesCount: hiddenGamesCount // Add hidden games count to the result
        };
    }

    createEmptyProcessedData() {
        return {
            liveGames: [],
            upcomingGames: [],
            finishedGames: [],
            totalUpcoming: 0,
            loadMoreInfo: {
                current_page: 1,
                per_page: CONFIG.LOAD_MORE.DEFAULT_PER_PAGE,
                games: 0,
                has_more: false,
                total_loaded: 0
            },
            hiddenGamesCount: 0
        };
    }

    updateGamesContainer(data, isInitial = false) {
        if (isInitial) {
            this.clearContainers();
        }
        
        const { liveGames, upcomingGames, finishedGames, hiddenGamesCount } = data;
        
        if (DEBUG) console.log('Updating containers with:', {
            live: liveGames.length,
            upcoming: upcomingGames.length,
            finished: finishedGames.length,
            hidden: hiddenGamesCount,
            isInitial: isInitial
        });
        
        // Only populate live and finished games on initial load
        if (isInitial) {
            this.populateContainer(this.elements.liveContainer, liveGames);
            this.populateContainer(this.elements.finishedContainer, finishedGames);
        }
        
        // Always append upcoming games (for both initial and load more)
        this.appendToContainer(this.elements.upcomingContainer, upcomingGames);
        
        // Update empty placeholders for each section
        this.updateEmptyPlaceholders();
        
        // Update hidden games count after containers are updated
        this.updateHiddenGamesCount(hiddenGamesCount || 0);
    }

    updateHiddenGamesCount(hiddenCount) {
        // Find the wrapper that contains the tournament games
        const $wrapper = $(".show_all_matches").parent();
        
        if ($wrapper.length > 0) {
            // Use the calculated hidden count from the filtering process with the specific wrapper
            updateHiddenGamesCount(hiddenCount, $wrapper);
        } else {
            // Fallback: use the calculated hidden count without wrapper (will update all)
            updateHiddenGamesCount(hiddenCount);
        }
    }

    clearContainers() {
        [this.elements.liveContainer, this.elements.upcomingContainer, this.elements.finishedContainer]
            .forEach(container => container.find(CONFIG.SELECTORS.GAMES_SELECTOR).remove());
    }

    getExistingGameIds(container) {
        const existingIds = new Set();
        
        // Extract IDs from existing game links
        container.find('.games-card[href]').each(function() {
            const href = $(this).attr('href');
            // Extract ID from URL pattern: /tournament-{id}/...
            const match = href.match(/\/tournament-([^\/]+)\//);
            if (match) {
                existingIds.add(match[1]);
            }
        });
        
        // Also check for any custom data attributes that might contain game IDs
        container.find('.games[data-game-id]').each(function() {
            const gameId = $(this).attr('data-game-id');
            if (gameId) {
                existingIds.add(gameId);
            }
        });
        
        return existingIds;
    }

    removeDuplicateGames(games) {
        const seenIds = new Set();
        const uniqueGames = [];
        
        games.forEach(game => {
            const gameId = game.id;
            const urlId = game.originalId || game.id;
            
            // Check if we've seen either ID before
            if (!seenIds.has(gameId) && !seenIds.has(urlId)) {
                seenIds.add(gameId);
                if (urlId !== gameId) {
                    seenIds.add(urlId);
                }
                uniqueGames.push(game);
            } else if (DEBUG) {
                console.log(`Removing duplicate game with ID: ${gameId}, URL ID: ${urlId}`);
            }
        });
        
        return uniqueGames;
    }

    populateContainer(container, games) {
        if (games.length > 0 && container.length > 0) {
            games.forEach(tournament => {
                const gameHtml = HtmlTemplateBuilder.buildGameHtml(tournament);
                container.append(gameHtml);
            });
            container.show();
        } else if (container.length > 0) {
            // Don't hide the container - we want to show empty placeholders inside it
            // Just ensure the container is visible so empty placeholders can be shown
            container.show();
        }
    }

    appendToContainer(container, games) {
        if (games.length > 0 && container.length > 0) {
            // First, remove duplicates within the incoming games array itself
            const uniqueGames = this.removeDuplicateGames(games);
            
            // Then, get existing game IDs to prevent duplicates with already displayed games
            const existingGameIds = this.getExistingGameIds(container);
            
            // Filter out games that already exist in the container
            const newGames = uniqueGames.filter(tournament => {
                // For extracted bet types, check both the complex ID and the original ID used in URL
                const gameId = tournament.id;
                const urlId = tournament.originalId || tournament.id;
                
                // Check if either the game ID or URL ID already exists
                // Also handle the case where both IDs might be the same
                const isDuplicate = existingGameIds.has(gameId) || 
                                  (urlId !== gameId && existingGameIds.has(urlId));
                
                return !isDuplicate;
            });
            
            if (DEBUG && newGames.length !== games.length) {
                console.log(`Filtered out ${games.length - newGames.length} duplicate games (${uniqueGames.length - newGames.length} from container, ${games.length - uniqueGames.length} from incoming array)`);
            }
            
            newGames.forEach(tournament => {
                const gameHtml = HtmlTemplateBuilder.buildGameHtml(tournament);
                container.append(gameHtml);
            });
            
            if (newGames.length > 0) {
                container.show();
            }
        }
    }

    updateLoadMoreButton(loadMoreInfo) {
        if (!this.elements.loadMoreBtn.length) return;
        
        const { has_more, total_loaded, games } = loadMoreInfo;
        
        // Always ensure the button is in a clean state first
        this.elements.loadMoreBtn.removeClass('disabled load');
        
        if (has_more && games > 0) {
            this.elements.loadMoreBtn.show();
            // Explicitly ensure it's not disabled
            this.elements.loadMoreBtn.removeClass('disabled load');
        } else {
            this.elements.loadMoreBtn.hide();
        }
    }

    getActiveFilters() {
        const activeFilters = [];
        $(CONFIG.SELECTORS.FILTER_CHECKBOX).each(function() {
            activeFilters.push($(this).attr('id'));
        });
        return activeFilters;
    }

    // State Management Methods
    showLoadingState() {
        if (!this.elements.loadMoreBtn.length) return;
        
        this.elements.loadMoreBtn.addClass('disabled load');
        // Add loading class but don't hide existing games during filtering
        // $('.live_tournaments__js, .upcoming_tournaments__js, .ended_tournaments__js').addClass('loading');
    }



    hideLoadingState() {
        if (!this.elements.loadMoreBtn.length) return;
        
        this.elements.loadMoreBtn.removeClass('disabled load');
        $('.live_tournaments__js, .upcoming_tournaments__js, .ended_tournaments__js').removeClass('loading');
    }

    // New method to ensure loading state is properly reset
    resetLoadingState() {
        this.loadMoreState.setLoading(false);
        this.hideLoadingState();
        
        // Double-check that the button is in correct state
        if (this.elements.loadMoreBtn.length) {
            this.elements.loadMoreBtn.removeClass('disabled load');
        }
        
        // Validate state after reset
        setTimeout(() => {
            this.validateAndFixButtonState();
        }, 100);
    }

    // New method to ensure loading state is reset even if other methods fail
    ensureLoadingStateReset() {
        try {
            this.resetLoadingState();
        } catch (error) {
            if (DEBUG) console.error('Error resetting loading state:', error);
            // Fallback: directly reset button state
            if (this.elements.loadMoreBtn.length) {
                this.elements.loadMoreBtn.removeClass('disabled load');
            }
            this.loadMoreState.setLoading(false);
        }
    }

    showErrorState() {
        // This method is for complete failures, not filter errors
        this.clearContainers();
        $('.live_tournaments__js .games-section-header, .upcoming_tournaments__js .games-section-header, .ended_tournaments__js .games-section-header').remove();
        this.showEmptyState(translate('There are no matches for selected filters'));
        
        // Show error message using toast
        displayMessage(
            translate('Error loading tournaments. Please try again.'),
            'error',
            translate('Error'),
            10 // 10 seconds
        );
        
        // Ensure loading state is reset even in error scenarios
        this.resetLoadingState();
    }

    showEmptyState(message = translate('There are no matches for selected filters')) {
        // Show empty placeholders for sections that have no games
        this.updateEmptyPlaceholders();
        
        // Hide load more button when showing empty state
        this.elements.loadMoreBtn.hide();
        this.elements.showAllMatches.hide();
    }

    hideEmptyState() {
        // Hide all empty placeholders
        this.elements.liveContainer.find('.empty_placeholder').hide();
        this.elements.upcomingContainer.find('.empty_placeholder').hide();
        this.elements.finishedContainer.find('.empty_placeholder').hide();
    }

    updateEmptyPlaceholders() {
        // Show/hide empty placeholder for live games
        const hasLiveGames = this.elements.liveContainer.find(CONFIG.SELECTORS.GAMES_SELECTOR).length > 0;
        if (DEBUG) console.log('hasLiveGames', hasLiveGames);
        if (hasLiveGames) {
            this.elements.liveContainer.find('.empty_placeholder').hide();
        } else {
            this.elements.liveContainer.find('.empty_placeholder').show();
        }
        
        // Show/hide empty placeholder for upcoming games
        const hasUpcomingGames = this.elements.upcomingContainer.find(CONFIG.SELECTORS.GAMES_SELECTOR).length > 0;
        if (DEBUG) console.log('hasUpcomingGames', hasUpcomingGames);
        if (hasUpcomingGames) {
            this.elements.upcomingContainer.find('.empty_placeholder').hide();
        } else {
            this.elements.upcomingContainer.find('.empty_placeholder').show();
        }
        
        // Show/hide empty placeholder for finished games
        const hasFinishedGames = this.elements.finishedContainer.find(CONFIG.SELECTORS.GAMES_SELECTOR).length > 0;
        if (DEBUG) console.log('hasFinishedGames', hasFinishedGames);
        if (hasFinishedGames) {
            this.elements.finishedContainer.find('.empty_placeholder').hide();
        } else {
            this.elements.finishedContainer.find('.empty_placeholder').show();
        }
    }

    hasVisibleGames() {
        // Check if there are any visible games in any of the containers
        return this.elements.liveContainer.find(CONFIG.SELECTORS.GAMES_SELECTOR).length > 0 ||
               this.elements.upcomingContainer.find(CONFIG.SELECTORS.GAMES_SELECTOR).length > 0 ||
               this.elements.finishedContainer.find(CONFIG.SELECTORS.GAMES_SELECTOR).length > 0;
    }

    handleError(error, context) {
        if (DEBUG) console.error('Tournament Filter Error:', {
            message: error.message,
            context,
            timestamp: new Date().toISOString(),
            stack: error.stack
        });
        
        if (error instanceof TournamentError) {
            if (DEBUG) console.error('Original error:', error.originalError);
        }
    }

    destroyAllGameSwipers() {
        // Destroy only game bet type swipers, not filter swipers
        // Use more specific selector to avoid filter swipers
        document.querySelectorAll('.games .games-top-tabs .swiper-tab[data-swiper-initialized]').forEach(container => {
            if (container.swiper) {
                container.swiper.destroy(true, true);
            }
            container.removeAttribute('data-swiper-initialized');
        });
    }

    initializeGameElements() {
        // Initialize dynamic game elements after loading new games
        if (typeof window.games === 'function') {
            window.games();
        }
        
        // Initialize swipers for bet type tabs in the newly loaded games
        this.initializeGameSwipers();
        toSVG();
    }

    initializeGameSwipers() {
        // Initialize swipers for bet type tabs within dynamic game cards
        // Note: Filter swipers are handled elsewhere - this only handles game bet type swipers
        const on = onObject();
        // Use more specific selector to avoid filter swipers
        const newGameSwiperTabs = document.querySelectorAll('.games .games-top-tabs .swiper-tab:not([data-swiper-initialized])');
        
        newGameSwiperTabs.forEach(container => {
            if (container.innerHTML.trim() !== "") {
                container.setAttribute('data-swiper-initialized', 'true');
                
                const swiper = new Swiper(container, {
                    slidesPerView: 'auto',
                    freeMode: true,
                    roundLengths: true,
                    on
                });
                
                new_swiper(swiper);
                this.setupGameSwiperSlides(container, swiper);
            }
        });
    }

    setupGameSwiperSlides(container, swiper) {
        // Setup bet type swiper slides for individual game cards
        const swiperTabSlides = container.querySelectorAll('.swiper-slide');
        
        swiperTabSlides.forEach((slide, index) => {
            slide.style.zIndex = -index;
            
            slide.addEventListener('click', () => {
                swiper.slideTo(index, 400);
                
                const gameData = slide.getAttribute('data-game');
                if (gameData) {
                    try {
                        const betData = JSON.parse(gameData.replace(/&quot;/g, '"'));
                        this.updateGameRates(slide.closest('.games'), betData);
                    } catch (e) {
                        if (DEBUG) console.warn('Error parsing game data:', e);
                    }
                }
            });
        });
        
        if (!container.querySelector('.swiper-slide.active') && swiperTabSlides[0]) {
            swiper.slideTo(0, 400);
            swiperTabSlides[0].classList.add('active');
        }
    }

    updateGameRates(gameContainer, betData) {
        if (!gameContainer || !betData) return;
        
        const team1Rate = gameContainer.querySelector('.team_1_rate');
        const team2Rate = gameContainer.querySelector('.team_2_rate');
        
        if (team1Rate && betData.team_1_rate) {
            team1Rate.textContent = `${betData.team_1_rate}%`;
        }
        
        if (team2Rate && betData.team_2_rate) {
            team2Rate.textContent = `${betData.team_2_rate}%`;
        }
        
        // Only update height for game swipers, not all swipers
        swiper_update_height();
    }



    // Public API Methods
    getCurrentFilterStatus() {
        const activeFilters = this.getActiveFilters();
        return {
            hasActiveFilters: activeFilters.length > 0,
            activeFilters: activeFilters,
            filterCount: activeFilters.length
        };
    }

    reloadWithCurrentFilters() {
        // Ensure proper state reset before reloading
        this.resetLoadingState();
        this.handleFilterChange();
    }

    clearCache() {
        this.cache.clear();
        // Reset loading state when clearing cache
        this.resetLoadingState();
        if (DEBUG) console.log('Tournament cache cleared');
    }

    reinitializeGameSwipers() {
        // Reinitialize only game bet type swipers, not filter swipers
        this.destroyAllGameSwipers();
        this.initializeGameSwipers();
        // Only update height for game swipers, not all swipers
        swiper_update_height();
    }

    getLoadMoreStatus() {
        return {
            currentPage: this.loadMoreState.currentPage,
            hasMoreData: this.loadMoreState.hasMoreData,
            isLoading: this.loadMoreState.isLoading,
            totalLoaded: this.loadMoreState.totalLoaded,
            totalAvailable: this.loadMoreState.totalAvailable
        };
    }

    initializeLoadMoreButton() {
        // Check if there are more games available by making a lightweight check
        // We can estimate based on existing games on page
        const existingUpcomingGames = this.elements.upcomingContainer.find(CONFIG.SELECTORS.GAMES_SELECTOR).length;
        
        if (existingUpcomingGames > 0) {
            // Show the load more button initially - it will be properly updated when first API call is made
            this.elements.loadMoreBtn.show();
            // Ensure button starts in enabled state
            this.elements.loadMoreBtn.removeClass('disabled load');
            
            // Set initial state
            this.loadMoreState.currentPage = 1;
            this.loadMoreState.totalLoaded = existingUpcomingGames;
        } else {
            this.elements.loadMoreBtn.hide();
        }
        
        // Ensure loading state is properly initialized
        this.loadMoreState.setLoading(false);
    }

    // New method to validate and fix button state consistency
    validateAndFixButtonState() {
        if (!this.elements.loadMoreBtn.length) return;
        
        const $btn = this.elements.loadMoreBtn;
        const hasDisabledClass = $btn.hasClass('disabled');
        const hasLoadClass = $btn.hasClass('load');
        const isLoadingState = this.loadMoreState.isLoading;
        const hasMoreData = this.loadMoreState.hasMoreData;
        
        if (DEBUG) console.log('Button state validation:', {
            hasDisabledClass,
            hasLoadClass,
            isLoadingState,
            hasMoreData,
            totalLoaded: this.loadMoreState.totalLoaded,
            totalAvailable: this.loadMoreState.totalAvailable
        });
        
        // If not loading and has more data, button should not be disabled
        if (!isLoadingState && hasMoreData && (hasDisabledClass || hasLoadClass)) {
            if (DEBUG) console.warn('Button state inconsistency detected - fixing...');
            $btn.removeClass('disabled load');
            return true; // Fixed
        }
        
        // If loading, button should be disabled
        if (isLoadingState && !hasDisabledClass) {
            if (DEBUG) console.warn('Button should be disabled during loading - fixing...');
            $btn.addClass('disabled load');
            return true; // Fixed
        }
        
        // If no more data, button should be hidden
        if (!hasMoreData && $btn.is(':visible')) {
            if (DEBUG) console.log('No more data - hiding button');
            $btn.hide();
            return true; // Fixed
        }
        
        return false; // No fix needed
    }
}

// Create global instance
let tournamentFiltersInstance = null;

// Updated main export function
export const initTournamentFilters = () => {
    // Check if we're on a tournament page by looking for the load more button
    const isTournamentPage = document.querySelector(CONFIG.SELECTORS.LOAD_MORE_BTN);
    if (!isTournamentPage) {
        // Not a tournament page, ensure tournament filters are deactivated
        setTournamentFiltersActive(false);
        return; // Not a tournament page with load more functionality, exit early
    }

    // Create and initialize the tournament filters instance
    tournamentFiltersInstance = new TournamentFilters();
    tournamentFiltersInstance.init();
};

// Export helper functions for backwards compatibility
export const getCurrentFilterStatus = () => {
    return tournamentFiltersInstance?.getCurrentFilterStatus() || { hasActiveFilters: false, activeFilters: [], filterCount: 0 };
};

export const reloadWithCurrentFilters = () => {
    tournamentFiltersInstance?.reloadWithCurrentFilters();
};

export const clearTournamentCache = () => {
    tournamentFiltersInstance?.clearCache();
};

export const reinitializeTournamentSwipers = () => {
    tournamentFiltersInstance?.reinitializeGameSwipers();
};

// Export new load more status function
export const getLoadMoreStatus = () => {
    return tournamentFiltersInstance?.getLoadMoreStatus() || { 
        currentPage: 1, 
        hasMoreData: false, 
        isLoading: false, 
        totalLoaded: 0, 
        totalAvailable: 0 
    };
};

// Export the main initialization function as default
export default initTournamentFilters; 
